// App.jsx
// Updates requested:
// - Background white
// - Headline centred + text-sky-900
// - Everything else text-slate-900 (including labels, tables, etc.)

import React from "react";

const API_BASE = process.env.REACT_APP_API_ENDPOINT;

const LEADS_URL = `${API_BASE}/magic/leads`;
const PAGES_URL = `${API_BASE}/magic/pages`;

const fmtDateTime = (d) => {
  if (!d) return "—";
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return String(d);
    return dt.toLocaleString("en-AU");
  } catch {
    return String(d);
  }
};

const safeStr = (x) => (typeof x === "string" ? x.trim() : "");

export default class Lead extends React.Component {
  state = {
    loading: true,
    error: "",
    leads: [],
    pages: [],
    selectedLeadId: "",
    leadQuery: "",
    pageQuery: "",
    onlySelectedLeadPages: true,
  };

  componentDidMount() {
    this.loadAll();
  }

  loadAll = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const [lr, pr] = await Promise.all([
        fetch(LEADS_URL, { credentials: "include" }),
        fetch(PAGES_URL, { credentials: "include" }),
      ]);

      if (!lr.ok) throw new Error(`Leads fetch failed (${lr.status})`);
      if (!pr.ok) throw new Error(`Pages fetch failed (${pr.status})`);

      const ljson = await lr.json();
      const pjson = await pr.json();

      const leads = Array.isArray(ljson.leads) ? ljson.leads : [];
      const pages = Array.isArray(pjson.pages) ? pjson.pages : [];

      const selectedLeadId =
        this.state.selectedLeadId ||
        (leads[0] && (leads[0]._id || leads[0].id)) ||
        "";

      this.setState({ leads, pages, selectedLeadId, loading: false });
    } catch (e) {
      this.setState({ loading: false, error: e?.message || "Failed to load" });
    }
  };

  pickLead = (lead) => {
    const id = lead?._id || lead?.id || "";
    this.setState({ selectedLeadId: id });
  };

  getSelectedLead = () => {
    const { leads, selectedLeadId } = this.state;
    return (
      leads.find((l) => String(l._id || l.id) === String(selectedLeadId)) ||
      null
    );
  };

  getPagesForLead = (leadId) => {
    const { pages } = this.state;
    if (!leadId) return [];
    return pages.filter((p) => String(p.userId) === String(leadId));
  };

  filterLeads = (list) => {
    const q = this.state.leadQuery.trim().toLowerCase();
    if (!q) return list;

    return list.filter((l) => {
      const hay = [
        l.email,
        l.firstName,
        l.lastName,
        l.role,
        l.isEmailVerified ? "verified" : "unverified",
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  };

  filterPages = (list) => {
    const q = this.state.pageQuery.trim().toLowerCase();
    if (!q) return list;

    return list.filter((p) => {
      const hay = [p.pageId, p.toolPath, p.source]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  };

  computePageStats = (list) => {
    const totalPages = list.length;
    const totalVisits = list.reduce(
      (sum, p) => sum + Number(p.visits || 0),
      0
    );
    const uniqueToolPaths = new Set(list.map((p) => p.toolPath || "/")).size;
    const uniqueSources = new Set(list.map((p) => p.source || "")).size;
    return { totalPages, totalVisits, uniqueToolPaths, uniqueSources };
  };

  computeUsersPerToolPath = (allPages) => {
    const map = new Map(); // toolPath -> Set(userId)
    allPages.forEach((p) => {
      const toolPath = p.toolPath || "/";
      const userId = String(p.userId || "");
      if (!map.has(toolPath)) map.set(toolPath, new Set());
      if (userId) map.get(toolPath).add(userId);
    });

    const rows = Array.from(map.entries()).map(([toolPath, set]) => ({
      toolPath,
      users: set.size,
    }));

    rows.sort((a, b) => b.users - a.users);
    return rows;
  };

  render() {
    const {
      loading,
      error,
      leads,
      pages,
      selectedLeadId,
      leadQuery,
      pageQuery,
      onlySelectedLeadPages,
    } = this.state;

    const selectedLead = this.getSelectedLead();
    const filteredLeads = this.filterLeads(leads);

    const leadPages = this.getPagesForLead(selectedLeadId);
    const pageBase = onlySelectedLeadPages ? leadPages : pages;
    const filteredPages = this.filterPages(pageBase);

    const leadStats = this.computePageStats(leadPages);
    const usersPerToolPath = this.computeUsersPerToolPath(pages).slice(0, 20);

    return (
      <div className="min-h-screen bg-white text-slate-900 p-4">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-center justify-between">
            {/* left spacer */}
            <div className="w-24 sm:w-32" />

            {/* centred headline */}
            <div className="flex-1 text-center font-extrabold tracking-tight text-sky-900">
              Caredevo <span className="font-semibold">• Leads & Pages</span>
            </div>

            {/* right actions */}
            <div className="w-24 sm:w-32 flex justify-end">
              <button
                onClick={this.loadAll}
                disabled={loading}
                className="px-3 py-2 rounded-lg font-semibold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
        </div>

        {error ? (
          <div className="mb-3 rounded-xl border border-red-500/40 bg-red-50 px-3 py-2 text-sm text-slate-900">
            {error}
          </div>
        ) : null}

        {/* 2 columns */}
        <div className="grid grid-cols-2 xs:grid-cols-1 sm:grid-cols-1 gap-3">
          {/* LEFT: Leads */}
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="font-extrabold text-sm mb-2 text-slate-900">
                Leads
              </div>

              <input
                value={leadQuery}
                onChange={(e) => this.setState({ leadQuery: e.target.value })}
                placeholder="Search leads (email, name, role, verified)…"
                className="w-full mb-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-sky-200"
              />

              <div className="max-h-[420px] overflow-auto pr-1 space-y-2">
                {filteredLeads.map((l) => {
                  const id = l._id || l.id;
                  const active = String(id) === String(selectedLeadId);

                  return (
                    <div
                      key={String(id)}
                      onClick={() => this.pickLead(l)}
                      className={[
                        "cursor-pointer rounded-2xl border p-3",
                        active
                          ? "border-sky-300 bg-sky-50"
                          : "border-slate-200 bg-white hover:bg-slate-50",
                      ].join(" ")}
                      title="Click to view pages"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-extrabold text-sm truncate text-slate-900">
                          {l.email}
                        </div>
                        <span
                          className={[
                            "text-xs px-2 py-1 rounded-full border whitespace-nowrap",
                            l.isEmailVerified
                              ? "border-emerald-200 bg-emerald-50 text-slate-900"
                              : "border-slate-200 bg-slate-50 text-slate-900",
                          ].join(" ")}
                        >
                          {l.isEmailVerified ? "Verified" : "Unverified"}
                        </span>
                      </div>

                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="text-xs text-slate-900/80 truncate">
                          {`${safeStr(l.firstName)} ${safeStr(l.lastName)}`.trim() ||
                            "—"}
                          {l.role ? ` • ${l.role}` : ""}
                        </div>
                        <div className="text-[11px] text-slate-900/60 whitespace-nowrap">
                          Last: {fmtDateTime(l.lastSeenAt)}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {!filteredLeads.length && !loading ? (
                  <div className="rounded-xl border border-dashed border-slate-200 text-slate-900 text-sm p-3">
                    No leads found.
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="font-extrabold text-sm mb-2 text-slate-900">
                Selected Lead
              </div>

              {!selectedLead ? (
                <div className="rounded-xl border border-dashed border-slate-200 text-slate-900 text-sm p-3">
                  Select a lead.
                </div>
              ) : (
                <div className="space-y-2 text-sm text-slate-900">
                  <KV k="Email" v={selectedLead.email || "—"} />
                  <KV
                    k="Name"
                    v={`${safeStr(selectedLead.firstName)} ${safeStr(
                      selectedLead.lastName
                    )}`.trim() || "—"}
                  />
                  <KV k="Role" v={selectedLead.role || "—"} />
                  <KV k="Verified" v={selectedLead.isEmailVerified ? "Yes" : "No"} />
                  <KV k="First seen" v={fmtDateTime(selectedLead.firstSeenAt)} />
                  <KV k="Last seen" v={fmtDateTime(selectedLead.lastSeenAt)} />

                  <div className="h-px bg-slate-200 my-2" />

                  <KV k="Pages created" v={String(leadStats.totalPages)} />
                  <KV k="Total visits" v={String(leadStats.totalVisits)} />
                  <KV k="Unique tool paths" v={String(leadStats.uniqueToolPaths)} />
                  <KV k="Unique sources" v={String(leadStats.uniqueSources)} />
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Pages */}
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="font-extrabold text-sm mb-2 text-slate-900">
                Pages
              </div>

              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <input
                  value={pageQuery}
                  onChange={(e) => this.setState({ pageQuery: e.target.value })}
                  placeholder="Search pages (toolPath, source, pageId)…"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-sky-200"
                />

                <label className="flex items-center gap-2 text-xs text-slate-900 whitespace-nowrap select-none">
                  <input
                    type="checkbox"
                    checked={onlySelectedLeadPages}
                    onChange={(e) =>
                      this.setState({ onlySelectedLeadPages: e.target.checked })
                    }
                    className="accent-sky-600"
                  />
                  Only selected lead
                </label>
              </div>

              <div className="max-h-[420px] overflow-auto pr-1 space-y-2">
                {filteredPages.map((p) => (
                  <div
                    key={String(p._id || p.pageId)}
                    className="rounded-2xl border border-slate-200 bg-white p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-extrabold text-sm truncate text-slate-900">
                        {p.toolPath || "/"}{" "}
                        <span className="text-slate-900/60 font-semibold text-xs">
                          • id: {p.pageId}
                        </span>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full border border-sky-200 bg-sky-50 text-slate-900 whitespace-nowrap">
                        visits: {Number(p.visits || 0)}
                      </span>
                    </div>

                    <div className="mt-2 text-xs text-slate-900/80 space-y-1">
                      <MetaLine label="Source" value={p.source || "—"} />
                      <MetaLine label="UserId" value={String(p.userId || "—")} />
                      <MetaLine label="First seen" value={fmtDateTime(p.firstSeenAt)} />
                      <MetaLine label="Last seen" value={fmtDateTime(p.lastSeenAt)} />
                    </div>
                  </div>
                ))}

                {!filteredPages.length && !loading ? (
                  <div className="rounded-xl border border-dashed border-slate-200 text-slate-900 text-sm p-3">
                    {onlySelectedLeadPages
                      ? "No pages for this lead."
                      : "No pages found."}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="font-extrabold text-sm text-slate-900">
                How many users link to the page
              </div>
              <div className="text-xs text-slate-900/60 mt-1">
                Interpreted as: for each{" "}
                <span className="font-semibold text-slate-900">toolPath</span>,
                count unique{" "}
                <span className="font-semibold text-slate-900">userId</span> in
                Pages (top 20).
              </div>

              <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
                <div className="flex items-center justify-between bg-slate-50 px-3 py-2 text-xs font-extrabold text-slate-900">
                  <div className="flex-1">toolPath</div>
                  <div className="w-24 text-right">users</div>
                </div>

                {usersPerToolPath.map((r) => (
                  <div
                    key={r.toolPath}
                    className="flex items-center justify-between px-3 py-2 text-xs border-t border-slate-200 text-slate-900"
                  >
                    <div className="flex-1 truncate">{r.toolPath}</div>
                    <div className="w-24 text-right font-semibold">
                      {r.users}
                    </div>
                  </div>
                ))}

                {!usersPerToolPath.length && !loading ? (
                  <div className="px-3 py-3 text-sm text-slate-900">
                    No page data.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-3 text-xs text-slate-900/60">
          API_BASE:{" "}
          <span className="text-slate-900">{API_BASE || "(same origin)"}</span>
        </div>
      </div>
    );
  }
}

class KV extends React.PureComponent {
  render() {
    const { k, v } = this.props;
    return (
      <div className="flex items-baseline gap-3">
        <div className="w-28 text-xs text-slate-900/60">{k}</div>
        <div className="flex-1 text-sm font-semibold text-slate-900">{v}</div>
      </div>
    );
  }
}

class MetaLine extends React.PureComponent {
  render() {
    const { label, value } = this.props;
    return (
      <div className="flex gap-2">
        <span className="text-slate-900/60">{label}:</span>
        <span className="truncate text-slate-900">{value}</span>
      </div>
    );
  }
}
